import { useContext, useRef, useState } from 'react';

import { Vertex2 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import type { CleanEdgeLabel } from '../helpers/generate-clean-edge-annotations';
import { useDraggableSvg } from '@r-services/ui-interactions';

export type RenderCleanEdgeLabelProps = {
	label: CleanEdgeLabel;
	fontSize: number;
};

type Offset = { dx: number; dy: number };

const parseOffset = (raw: string | undefined): Offset => {
	if (!raw) return { dx: 0, dy: 0 };
	try {
		const parsed = JSON.parse(raw) as Partial<Offset>;
		return { dx: Number(parsed.dx) || 0, dy: Number(parsed.dy) || 0 };
	} catch {
		return { dx: 0, dy: 0 };
	}
};

/**
 * A single draggable "CLEAN EDGE" label. Its default position and rotation come from the derived
 * geometry; a manual drag (model-space offset) and a manual rotation (absolute degrees) are each
 * persisted in the kitchen's blueprintOverrides (keyed by the label id) so they survive re-renders
 * and export — the same override store the EditableText fields use. On hover it exposes a rotate
 * (↻, +90° per click) and a delete (✕) control. Read-only pages render it static.
 */
export const RenderCleanEdgeLabel = (props: RenderCleanEdgeLabelProps): React.ReactElement => {
	const { label, fontSize } = props;
	const { controller } = useContext(AppContext);
	const { kitchen, options } = controller;
	const groupRef = useRef<SVGGElement>(null);

	const overrideKey = `cleanEdge.pos.${label.id}`;
	const deletedKey = `cleanEdge.deleted.${label.id}`;
	const rotationKey = `cleanEdge.rot.${label.id}`;
	const { dx, dy } = parseOffset(kitchen?.blueprintOverrides?.[overrideKey]);
	const storedRotation = kitchen?.blueprintOverrides?.[rotationKey];
	const [pos, setPos] = useState({ x: label.x + dx, y: label.y + dy });
	const [rotationDeg, setRotationDeg] = useState(storedRotation !== undefined ? Number(storedRotation) : label.rotation);
	const [hovered, setHovered] = useState(false);

	const draggable = !options.readonly;
	const isDeleted = Boolean(kitchen?.blueprintOverrides?.[deletedKey]);
	// Reuses the "hidden dimensions" show-all toggle (#354) to reveal-then-restore a deleted label, same pattern dimension-line.tsx uses for hidden dimensions
	const showAllHidden = options.showAllDimensions ?? false;

	useDraggableSvg({
		ref: groupRef,
		enabled: [draggable, draggable],
		cursor: ['inherit', 'move'],
		position: pos,
		bBox: { origin: new Vertex2(), size: { width: 1000, height: 1000, depth: 0 }, padding: [0, 0, 0, 0] },
		onDragging: (event) => {
			if (!event) return true;
			setPos(event.position.new);
			return true;
		},
		onDragEnd: (event) => {
			if (!event) return true;
			const { new: next } = event.position;
			kitchen?.setBlueprintOverride(overrideKey, JSON.stringify({ dx: next.x - label.x, dy: next.y - label.y }));
			return true;
		}
	});

	// Deleted labels are suppressed unless the hidden-dimensions toggle is on; the drag hook above
	// still runs either way so hook order stays stable.
	if (isDeleted && !showAllHidden) return <g ref={groupRef} />;

	const rotation = rotationDeg !== 0 ? `rotate(${rotationDeg}, ${pos.x}, ${pos.y})` : undefined;
	const ghostOpacity = isDeleted ? 0.35 : 1;

	// Only the text rotates; the hover controls stay pinned upper-right of the anchor so repeated
	// rotate clicks don't chase a control that orbits with the text.
	const rotateX = pos.x + fontSize;
	const deleteX = pos.x + (fontSize * 2.4);
	const controlY = pos.y - fontSize;

	// Bridges the gap between the label anchor and the hover controls so the pointer never leaves the
	// <g>'s painted hit area while crossing from one to the other.
	const bridgePad = fontSize / 2;
	const controlBridge = draggable && (
		<rect
			x={Math.min(pos.x, rotateX) - bridgePad}
			y={Math.min(pos.y, controlY) - bridgePad}
			width={Math.abs(deleteX - pos.x) + (bridgePad * 2)}
			height={Math.abs(controlY - pos.y) + (bridgePad * 2)}
			fill="transparent"
			style={{ pointerEvents: 'auto' }}
		/>
	);

	return (
		<g ref={groupRef} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
			<text
				x={pos.x}
				y={pos.y}
				fill="#C00000"
				fontSize={fontSize}
				fontWeight="bold"
				textAnchor="middle"
				opacity={ghostOpacity}
				style={{ userSelect: 'none', cursor: draggable && isDeleted ? 'pointer' : undefined }}
				transform={rotation}
				onPointerDown={draggable && isDeleted ? (event) => { event.stopPropagation(); kitchen?.clearBlueprintOverride(deletedKey); } : undefined}
			>
				{label.text}
			</text>
			{!isDeleted && controlBridge}
			{draggable && !isDeleted && hovered && (
				<text
					x={rotateX}
					y={controlY}
					fill="#C00000"
					fontSize={fontSize}
					fontWeight="bold"
					textAnchor="middle"
					style={{ userSelect: 'none', cursor: 'pointer' }}
					onPointerDown={(event) => {
						event.stopPropagation();
						const next = (((rotationDeg + 90) % 360) + 360) % 360;
						setRotationDeg(next);
						kitchen?.setBlueprintOverride(rotationKey, String(next));
					}}
				>
					↻
				</text>
			)}
			{draggable && hovered && (
				<text
					x={deleteX}
					y={controlY}
					fill="#C00000"
					fontSize={fontSize}
					fontWeight="bold"
					textAnchor="middle"
					style={{ userSelect: 'none', cursor: 'pointer' }}
					onPointerDown={(event) => {
						event.stopPropagation();
						kitchen?.setBlueprintOverride(deletedKey, '1');
					}}
				>
					✕
				</text>
			)}
		</g>
	);
};
